import { pxToRem, svgColor, svgUri } from '../../helpers'
import { List } from '../List'
import { IconClose, StyleFn } from '..'

export const IconChevrondown = {
  data: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M11.9999 13.9394L17.4696 8.46973L18.5303 9.53039L11.9999 16.0607L5.46961 9.53039L6.53027 8.46973L11.9999 13.9394Z" fill="#080341"></path> </g></svg>',
}

export const IconChevronup = {
  data: '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M6 15L12 9L18 15" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>',
}

export const SideNav: StyleFn = (theme) => {
  const { color, elevation, font, size, responsive } = theme
  const { root: list, item: listItem, item_disabled } = List(theme)
  const defaultTextColor = color.type === 'light' ? color.palette.coolgrey[90] : color.palette.white
  const itemsTextColor = color.type === 'light' ? color.palette.primaryblue[60] : color.palette.white

  const prepQuery = (value: string) => `@media (max-width:${value}px)`

  // recursive category level styles
  const getCategoryLevels = (levels: number) => {
    let currentLevel = 1
    const getLevel = (): any => ({
      '& $category': {
        '& $itemLink': {
          paddingLeft: pxToRem(16 + currentLevel * 8),
        },
        '& $section $itemLink': {
          paddingLeft: pxToRem(24 + currentLevel * 8 + 2),
          '&:after': { left: pxToRem(16 + currentLevel * 8) },
        },
        ...(currentLevel++ < levels && getLevel()),
      },
    })
    return getLevel()
  }
  return {
    root: {
      height: '100%',
      overflowY: 'auto',
      width: pxToRem(224),
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: color.getModalValues(color.background.primary),
      color: defaultTextColor,
      boxShadow: elevation.level3,
      '& > $section': {
        paddingTop: size.spacing[8],
      },
      ...Object.keys(responsive.breakpoint).reduce(
        (acc, bp) => ({
          ...acc,
          [prepQuery((responsive.breakpoint as any)[bp])]: {
            [`&$width-${bp}`]: {
              width: '100vw',
              zIndex: elevation.zindex.drawer,
            },
            '& $titleSection': {
              display: 'flex',
            },
          },
        }),
        {},
      ),
    },
    section: {
      ...list,
      color: itemsTextColor,
      padding: 0,
    },
    item: {
      position: 'relative',
      fontWeight: font.weight.regular,
    },
    itemLink: {
      ...listItem,
      justifyContent: 'space-between',
      padding: `${size.spacing[12]} ${size.spacing[16]}`,
      textDecoration: 'none',
      color: itemsTextColor,
      '& svg, & i': {
        height: font.size.medium,
        lineHeight: font.size.medium,
        fontSize: font.size.medium,
        alignSelf: 'center',
      },
    },
    _large: {
      '& $itemLink': {
        fontSize: font.size.large,
        '& svg, & i': {
          height: font.size['heading-5'],
          fontSize: font.size['heading-5'],
        },
      },
    },
    item_disabled,
    item_selected: {
      fontWeight: font.weight.bold,
      '& > $itemLink, & > $itemLink:hover': {
        backgroundColor: color.background.dark2,
        color: color.palette.white,
      },
    },
    category: {
      extend: 'item',
      '& > $itemLink': {
        // FIXME would be better managed with an ":after" pseudo class
        // that would fix the react storybook accessibility check about color contrast
        backgroundImage: svgUri(svgColor(IconChevrondown.data, itemsTextColor)),
        backgroundPosition: 'right 1rem top 1rem',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '1rem',
        [`@media (max-width:${responsive.breakpoint.xs}px)`]: {
          backgroundPosition: 'right 1rem top 1rem',
          backgroundSize: '1.5rem',
        },
      },
      '& $section $itemLink': {
        paddingLeft: `calc(${size.spacing[24]} + 2px)`,
      },
      ...getCategoryLevels(4),
    },
    category_closed: {
      '& > $section': {
        display: 'none',
        height: 0,
      },
      '&$category_selected > $itemLink': {
        backgroundImage: svgUri(svgColor(IconChevrondown.data, color.palette.white)),
      },
    },
    category_opened: {
      '& > $itemLink': {
        fontWeight: font.weight.bold,
        backgroundImage: svgUri(svgColor(IconChevronup.data, itemsTextColor)),
      },
      '&$category_selected > $itemLink': {
        backgroundImage: svgUri(svgColor(IconChevronup.data, color.palette.white)),
      },
    },
    category_current: {
      '& $item $itemLink': {
        '&:after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: size.spacing[16],
          bottom: 0,
          borderLeft: `solid 2px ${color.type === 'dark' ? color.palette.coolgrey[70] : color.palette.warmgrey[20]}`,
        },
      },
      '& $item_selected > $itemLink:after': {
        borderLeft: `solid 2px ${color.palette.coolgrey[40]} !important`,
      },
    },
    category_selected: {
      '& > $itemLink, & > $itemLink:hover': {
        backgroundColor: color.background.dark2,
        color: color.palette.white,
      },
    },
    titleSection: {
      display: 'none',
      justifyContent: 'space-between',
      alignItems: 'end',
      listStyleType: 'none',
      padding: '1rem',
      margin: '0',
    },
    title: {
      fontSize: font.size['heading-4'],
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      margin: 'auto 0px',
      color: color.type === 'light' ? color.palette.primaryblue[60] : color.palette.white,
    },
    close: {
      cursor: 'pointer',
      height: size.spacing['48'],
      width: size.spacing['48'],
      backgroundImage: svgUri(svgColor(IconClose.data, itemsTextColor)),
      backgroundRepeat: 'no-repeat',
      alignSelf: 'center',
    },
    headerPadding: {
      paddingTop: size.spacing[56],
    },
    'width-xxs': {},
    'width-xs': {},
    'width-sm': {},
    'width-md': {},
    'width-lg': {},
    'width-xl': {},
    'width-xxl': {},
  }
}
